import React, { useState } from "react";
import {
  Paper,
  Grid,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  LinearProgress,
} from "@material-ui/core";
import UpdateIcon from "@material-ui/icons/Update";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import { useCommissionContext } from "../../../../contexts/CommissionContext";
import commissionService from "../../../../services/commissions.service";

/**
 * Component for managing structure operations related to structure levels.
 * Provides functionality to update structure levels with confirmation dialog.
 * Utilizes the CommissionContext for context-specific actions like showing snackbars.
 */
export default function StructureOperations() {
  const { showSnackbar } = useCommissionContext();

  // Loading state
  const [loadingStructure, setLoadingStructure] = useState(false);

  // Status message
  const [statusStructure, setStatusStructure] = useState("");

  // Confirmation dialog
  const [confirmStructure, setConfirmStructure] = useState(false);
  const { cutoffDate } = useCommissionContext();

  /**
   * Handles updating structure levels
   * Initiates the update and shows progress indicator
   */
  const handleUpdateStructureLevels = async () => {
    try {
      setLoadingStructure(true);
      setStatusStructure(
        "Structure levels update in progress. This may take up to 5 minutes to complete in the background..."
      );
      setConfirmStructure(false);

      const result = await commissionService.updateStructureLevels(cutoffDate);

      if (result.success) {
        // Show success message but keep loading state active
        showSnackbar("Structure levels update started successfully", "success");

        // Auto-reset after estimated duration (converted to ms) + buffer
        const durationInMs = parseEstimatedDuration(result.estimatedDuration);
        setTimeout(() => {
          setLoadingStructure(false);
          setStatusStructure("Structure levels update completed");
          showSnackbar("Structure levels update completed", "success");
        }, durationInMs);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error("Error updating structure levels:", error);
      setStatusStructure(`Failed: ${error.message}`);
      setLoadingStructure(false);
      showSnackbar(
        `Failed to update structure levels: ${error.message}`,
        "error"
      );
    }
  };

  /**
   * Parses an estimated duration string and converts it to milliseconds.
   * Extracts the highest number from a string like "3-4 minutes" and
   * calculates the duration in milliseconds, adding a 30-second buffer.
   * If parsing fails, defaults to a duration of 6 minutes.
   *
   * @param {string} durationStr - The duration string to parse.
   * @returns {number} The duration in milliseconds.
   */

  const parseEstimatedDuration = durationStr => {
    try {
      // Extract highest number from string like "3-4 minutes"
      const match = durationStr.match(/\d+/g);
      if (match && match.length) {
        // Get the highest number in the string
        const minutes = Math.max(...match.map(Number));
        // Convert to ms and add a 30-second buffer
        return minutes * 60 * 1000 + 30000;
      }
    } catch (err) {
      console.error("Error parsing duration:", err);
    }
    // Default fallback: 6 minutes (5 minutes plus buffer)
    return 6 * 60 * 1000;
  };

  return (
    <Paper elevation={1} style={{ padding: 20, marginBottom: 20 }}>
      <Typography variant="h6" gutterBottom>
        <UpdateIcon style={{ verticalAlign: "middle", marginRight: 8 }} />
        Structure Operations
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Update structure levels for the dealer hierarchy
      </Typography>

      <Grid container spacing={3}>
        {/* Update Structure Levels Button */}
        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            disabled={loadingStructure}
            onClick={() => setConfirmStructure(true)}
            startIcon={
              loadingStructure ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <AccountTreeIcon />
              )
            }
          >
            {loadingStructure
              ? "Updating Structure Levels..."
              : "Update Structure Levels"}
          </Button>

          {/* Progress indicator */}
          {loadingStructure && (
            <div style={{ marginTop: 16 }}>
              <LinearProgress
                variant="indeterminate"
                style={{ height: 3, borderRadius: 3 }}
              />
              {statusStructure && (
                <Typography
                  variant="caption"
                  display="block"
                  style={{
                    marginTop: 8,
                    textAlign: "center",
                    padding: 8,
                    borderRadius: 4,
                  }}
                >
                  {statusStructure}
                </Typography>
              )}
            </div>
          )}
        </Grid>
      </Grid>

      {/* Confirmation Dialog for Structure Levels */}
      <Dialog
        open={confirmStructure}
        onClose={() => setConfirmStructure(false)}
      >
        <DialogTitle>Confirm Structure Levels Update</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to update structure levels? This operation may
            take up to 5 minutes to complete.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmStructure(false)} color="default">
            Cancel
          </Button>
          <Button
            onClick={handleUpdateStructureLevels}
            color="primary"
            variant="contained"
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
